import React, { useContext, useState } from 'react';
import Geocode from "react-geocode";
import config from '../../config/config';
import SearchBox from '../components/SearchBox';

import { GeolocationContext } from '../contexts/geolocation-context';
import { LocalizationContext } from '../contexts/localization-context';
import { MapContext } from '../contexts/map-context';

import useVisible from '../hooks/useVisible';

const debounce = require('debounce');

const SearchContainer = ({ googleMapsApiKey }) => {

    const [geolocationState, dispatchToGeolocation] = useContext(GeolocationContext);
    const [localizationState] = useContext(LocalizationContext);
    const [mapRef] = useContext(MapContext);
    const [searchedLocation, setSearchedLocation] = useState("");
    const [searchBoxIsFocused, setSearchBoxIsFocused] = useState(false);
    const { ref, isVisible, setIsVisible } = useVisible(false);
    const [selectedCountryName, setSelectedCountryName] = useState(geolocationState.countryName);
    const internationalCountryName = "International";
    let shouldShowCountrySelector = false;
    const showCountrySelectorElem = document.getElementById('showCountrySelector');
    if (showCountrySelectorElem !== null) {
        shouldShowCountrySelector = showCountrySelectorElem.value.toLowerCase() === "true";
    }

    const handleCountrySelect = (country) => {
        if (country.name !== selectedCountryName) {
            setSelectedCountryName(country.name);
            Geocode.setApiKey(googleMapsApiKey);
            if (country.name !== internationalCountryName) {
                Geocode.fromAddress(`${country.name} country`).then(
                    (response) => {
                        dispatchToGeolocation({
                            type: "SET_MAP_BOUNDS",
                            payload: {
                                bounds: response.results[0].geometry.viewport,
                            },
                        });
                        return;
                    }
                );
            } else {
                dispatchToGeolocation({
                    type: "SET_MAP_BOUNDS",
                    payload: {
                        bounds: {
                            southwest: { lat: -45, lng: -60 },
                            northeast: { lat: 75, lng: 60 },
                        },
                    },
                });
            }
        }
        setIsVisible(false);
    }

    const handleInputChange = debounce((searchText) => {
        setSearchedLocation(searchText);
    }, 400);

    const getSearchedLocationCoordinates = () => {
        /* Google Analytics */
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({
            event: "store_search",
            eventCategory: "Store Locator",
            eventAction: "Search",
            eventLabel: searchedLocation,
        });

        Geocode.setApiKey(googleMapsApiKey);
        Geocode.fromAddress(`${searchedLocation}${selectedCountryName === internationalCountryName ? "" : " " + selectedCountryName}`).then(
            response => {
                const bounds = new google.maps.LatLngBounds();
                bounds.extend(response.results[0].geometry.viewport.southwest);
                bounds.extend(response.results[0].geometry.viewport.northeast);
                mapRef.fitBounds(bounds);
            },
            error => {
                console.error(error);
            }
        );
    }

    const onUseMyPositionButtonClick = () => {
        setSearchedLocation(geolocationState.userAddress);
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(geolocationState.userBounds.southwest);
        bounds.extend(geolocationState.userBounds.northeast);
        mapRef.fitBounds(bounds);
    }

    const { countries } = localizationState;

    return (
        <div className="relative z-10 -mb-40 xs:-mb-16">
            <div className="flex justify-center custom-container">
                <div className="flex w-full p-20 bg-white sm:flex-wrap sm:relative sm:justify-end xs:flex-col xs:p-12 max-w-880 search-box-shadow">
                    {shouldShowCountrySelector &&
                        <div className="w-1/3 mr-16 select sm:w-2/5 sm:max-w-map-select xs:max-w-full xs:w-full sm:mr-12 xs:mr-0">
                            <div className="select__selected-option" onClick={() => setIsVisible(true)}>{selectedCountryName}</div>
                            {isVisible &&
                                <div className="select__container" ref={ref}>
                                    <div className="select__wrapper">
                                        <ul>
                                            {countries.map((country) =>
                                                <li
                                                    key={country.name}
                                                    className={selectedCountryName === country.name ? "select__option selected" : "select__option"}
                                                    onClick={() => handleCountrySelect(country)}
                                                >
                                                    {country.name}
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            }
                        </div>
                    }
                    <div className={searchBoxIsFocused ? "flex relative sm:static mr-16 sm:mr-0 w-2/3 sm:w-3/5 xs:w-full xs:my-12 border border-black rounded-4" : "flex relative sm:static mr-16 sm:mr-0 w-2/3 sm:w-3/5 xs:w-full xs:my-12 border border-gray-brown-dark rounded-4"}>
                        <SearchBox
                            className="flex-1 h-40 p-12 leading-none rounded-4 max-h-38 placeholder-gray-brown-dark focus:outline-none"
                            onChange={(searchText) => handleInputChange(searchText)}
                            value={searchedLocation}
                            onFocus={() => setSearchBoxIsFocused(true)}
                            onBlur={() => setSearchBoxIsFocused(false)}
                            placeholder={localizationState.searchBoxPlaceholder}
                            onPressEnter={getSearchedLocationCoordinates}
                        />
                        <div className="flex items-center pr-12 sm:absolute sm:left-20 sm:bottom-28 xs:left-12 xs:bottom-20 sm:centered-y 2xs:max-w-200 4xs:max-w-150">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                className={geolocationState.geolocationErrorMessage.length === 0 ? "min-w-24 cursor-pointer" : "min-w-24 opacity-25 disabled"}
                                onClick={geolocationState.geolocationErrorMessage.length === 0 ? onUseMyPositionButtonClick : null}
                            >
                                <g fill="none">
                                    <g fill="#CF002C">
                                        <g>
                                            <g>
                                                <path d="M12 1c.552 0 1 .447 1 1v1.29c4.039.46 7.25 3.671 7.711 7.71H22c.552 0 1 .447 1 1 0 .552-.448 1-1 1h-1.289c-.461 4.038-3.672 7.25-7.711 7.711v1.29c0 .551-.448 1-1 1s-1-.449-1-1v-1.29c-4.039-.46-7.25-3.673-7.711-7.71H2c-.552 0-1-.449-1-1 0-.554.448-1 1-1h1.289C3.75 6.96 6.961 3.75 11 3.288V2c0-.553.448-1 1-1zm1 4.31V6c0 .552-.448 1-1 1s-1-.448-1-1v-.69C8.067 5.746 5.747 8.065 5.31 11H6c.552 0 1 .447 1 1 0 .552-.448 1-1 1h-.69c.437 2.933 2.757 5.253 5.69 5.69V18c0-.553.448-1 1-1s1 .447 1 1v.69c2.934-.437 5.253-2.757 5.69-5.69H18c-.552 0-1-.448-1-1 0-.553.448-1 1-1h.69c-.437-2.934-2.756-5.253-5.69-5.69zM12 9c.552 0 1 .447 1 1v1h1c.552 0 1 .447 1 1 0 .553-.448 1-1 1h-1v1c0 .553-.448 1-1 1s-1-.447-1-1v-1h-1c-.552 0-1-.447-1-1 0-.553.448-1 1-1h1v-1c0-.553.448-1 1-1z" transform="translate(-879 -211) translate(282 183) translate(597 28)" />
                                            </g>
                                        </g>
                                    </g>
                                </g>
                            </svg>
                            <div
                                className={geolocationState.geolocationErrorMessage.length === 0 ? "ml-6 text-14 leading-none cursor-pointer whitespace-no-wrap 2xs:whitespace-normal" : "ml-6 text-14 leading-none opacity-25 disabled whitespace-no-wrap 2xs:whitespace-normal"}
                                onClick={geolocationState.geolocationErrorMessage.length === 0 ? onUseMyPositionButtonClick : null}
                                title={geolocationState.geolocationErrorMessage.length > 0 ? geolocationState.geolocationErrorMessage : null}
                            >
                                {localizationState.useMyPositionButton}
                            </div>
                        </div>
                    </div>

                    <div className="sm:mt-12 xs:flex xs:justify-end">
                        <div
                            className="btn btn--primary btn--small btn--no-wrap"
                            onClick={getSearchedLocationCoordinates}
                        >
                            {localizationState.searchButton}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    )
}

export default SearchContainer;
